import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from '../providers/TranslationProvider';
import {
	getNotificationsUnreadPoll,
	type NotificationsDataSourceModel,
	type NotificationsPanelItemModel,
} from './components/NotificationsPanel';
import {
	acquireOpenTabLeadership,
	claimNotificationsToShow,
	isOpenTabModeOn,
	OPEN_TAB_NOTIFICATIONS_FETCH_LIMIT,
	type OpenTabAppModel,
	type OpenTabLeadershipModel,
	openTabStorage,
	resumeOpenTabMarker,
	setOpenTabMode,
	subscribeOpenTabMode,
	withOpenTabNotificationsLock,
} from './openTabNotifications';

/** Whether open-tab mode is on for this owner, kept current across tabs. */
export function useOpenTabMode(
	app: OpenTabAppModel,
	owner: string | null,
): boolean {
	const subscribe = useCallback(
		(listener: () => void) => subscribeOpenTabMode(app, listener),
		[app],
	);
	return useSyncExternalStore(
		subscribe,
		() => isOpenTabModeOn(app, owner),
		() => false,
	);
}

export interface OpenTabNotifierOptionsModel {
	app: OpenTabAppModel;
	/** Who is signed in (see openTabNotifications.ts); null watches nothing. */
	owner: string | null;
	/** The bell's data source; its shared unread poll is the clock. */
	source: NotificationsDataSourceModel;
	/** Where a notification opens, the same map the bell uses. */
	resolvePath: (entityType: string | null, entityId: number | null) => string | null;
	/** A browser (not the native app) whose notification permission is granted. */
	canShow: () => boolean;
	/** Whether this browser holds a real push subscription. */
	isPushSubscribed: () => Promise<boolean>;
	/** False once the session is gone, so a sign-out's last poll reads nothing. */
	isSessionActive: () => boolean;
	/** Open a path in the app (the page-level Notification fallback's click). */
	onNavigate: (path: string) => void;
}

/**
 * PD-17322 — browser notifications while a tab of the app is open, for a
 * browser that allows notifications but cannot receive Web Push (Brave ships
 * with its push service off). Call it from one component mounted once for
 * the signed-in user.
 *
 * It rides the bell's shared unread poll instead of adding its own timer: on
 * every tick it reads the newest page of bell rows and shows the ones no tab
 * has shown yet. Every tick, not only when the unread count rises, because a
 * read and a new arrival in the same minute leave the count flat. A realtime
 * nudge that refreshes the bell (the CRM's notifyIndicator) is a tick too.
 * Mirroring the bell means Pause all and the In app choices apply, because
 * the server only writes a bell row the user asked for.
 *
 * - One reader: only the tab holding the leader lock
 *   (acquireOpenTabLeadership) makes that request; other tabs wait and take
 *   over when it closes.
 * - No replay: the first time this browser watches, every row already on
 *   the bell is marked handled. After that a starting tab keeps the shared
 *   marker and shows only rows from the last few minutes it has not seen
 *   (resumeOpenTabMarker), so one that arrived while the page was loading is
 *   not lost and a long absence does not replay a backlog.
 * - One tab per notification: the "handled up to" marker is shared through
 *   localStorage and read-and-advanced under a Web Lock, so the first tab to
 *   look claims a notification and the others find nothing left. Each
 *   notification also carries a tag of its own id, so even without Web Locks
 *   the browser replaces a duplicate instead of showing a second one.
 * - Real push wins: if this browser holds a push subscription, the service
 *   worker already shows every notification, so open-tab mode is switched
 *   off rather than doubling them.
 */
export function useOpenTabNotifier(options: OpenTabNotifierOptionsModel): void {
	const { t } = useTranslation();
	const { app, owner, source } = options;
	const on = useOpenTabMode(app, owner);
	// Read through refs so a language change or new callback identities do
	// not restart the watcher.
	const tRef = useRef(t);
	const optionsRef = useRef(options);
	useEffect(() => {
		tRef.current = t;
		optionsRef.current = options;
	});

	useEffect(() => {
		if (owner == null || !on || !optionsRef.current.canShow()) {
			return;
		}
		const watchedOwner = owner;
		const poll = getNotificationsUnreadPoll(source);
		let cancelled = false;
		let unsubscribe: (() => void) | null = null;
		let leadership: OpenTabLeadershipModel | null = null;
		// One check at a time in this tab; a tick during a check queues one
		// more behind it.
		let queue: Promise<void> = Promise.resolve();

		// The newest page only: one small request a minute from the leader.
		const readPage = async (): Promise<
			ReadonlyArray<NotificationsPanelItemModel>
		> => source.list({ limit: OPEN_TAB_NOTIFICATIONS_FETCH_LIMIT });

		const show = async (
			notifications: ReadonlyArray<NotificationsPanelItemModel>,
		): Promise<void> => {
			for (const notification of notifications) {
				await showOpenTabNotification(
					app,
					notification,
					tRef.current,
					optionsRef.current.resolvePath,
					(path) => {
						optionsRef.current.onNavigate(path);
					},
				);
			}
		};

		const check = async (): Promise<void> => {
			// A signed-out tab (the poll's reset emits once more) has nothing
			// to read.
			if (cancelled || !optionsRef.current.isSessionActive()) {
				return;
			}
			const page = await readPage();
			if (cancelled || !optionsRef.current.canShow()) {
				return;
			}
			const toShow = await withOpenTabNotificationsLock(
				app,
				watchedOwner,
				() => claimNotificationsToShow(openTabStorage(), app, watchedOwner, page),
			);
			await show(toShow);
		};

		const start = async (): Promise<void> => {
			if (await optionsRef.current.isPushSubscribed()) {
				setOpenTabMode(app, watchedOwner, false);
				return;
			}
			const page = await readPage();
			if (cancelled) {
				return;
			}
			const caughtUp = await withOpenTabNotificationsLock(
				app,
				watchedOwner,
				() =>
					resumeOpenTabMarker(
						openTabStorage(),
						app,
						watchedOwner,
						page,
						Date.now(),
					),
			);
			if (cancelled) {
				return;
			}
			await show(caughtUp);
			leadership = acquireOpenTabLeadership(app, watchedOwner);
			// subscribe() hands over the value it already has synchronously;
			// that is not a tick, so it is skipped.
			let subscribing = true;
			unsubscribe = poll.subscribe(() => {
				if (subscribing || leadership?.isLeader() !== true) {
					return;
				}
				queue = queue.then(check).catch((error: unknown) => {
					console.debug('[useOpenTabNotifier] Check failed', error);
				});
			});
			subscribing = false;
		};

		start().catch((error: unknown) => {
			console.debug('[useOpenTabNotifier] Could not start', error);
		});

		return (): void => {
			cancelled = true;
			unsubscribe?.();
			leadership?.release();
		};
	}, [app, owner, on, source]);
}

/**
 * Show one bell row as a browser notification. Through the app's service
 * worker when it is registered, so a click is handled by its
 * notificationclick (focus the tab, open the page) exactly like a push; the
 * data carries `url` plus `entityType` / `entityId`, which is what both
 * apps' workers read. Otherwise with the page-level Notification
 * constructor, which some browsers (Chrome on Android) refuse, hence the try.
 */
async function showOpenTabNotification(
	app: OpenTabAppModel,
	notification: NotificationsPanelItemModel,
	t: (text: string) => string,
	resolvePath: (entityType: string | null, entityId: number | null) => string | null,
	onOpen: (path: string) => void,
): Promise<void> {
	const path = resolvePath(notification.entityType, notification.entityId);
	const title = t(notification.title);
	const options: NotificationOptions = {
		body: notification.body != null ? t(notification.body) : '',
		// One tag per notification: a second tab showing the same one replaces
		// it rather than adding a duplicate.
		tag: `${app.tagPrefix}-${notification.id}`,
		icon: '/favicon.ico',
		badge: '/favicon.ico',
		data: {
			url: path,
			entityType: notification.entityType,
			entityId: notification.entityId,
		},
	};

	try {
		const registration =
			'serviceWorker' in navigator
				? await navigator.serviceWorker.getRegistration('/')
				: undefined;
		if (registration?.active != null) {
			await registration.showNotification(title, options);
			return;
		}
	} catch (error) {
		console.debug(
			'[useOpenTabNotifier] Service worker notification failed',
			error,
		);
	}

	try {
		const shown = new Notification(title, options);
		shown.onclick = (): void => {
			window.focus();
			shown.close();
			if (path != null) {
				onOpen(path);
			}
		};
	} catch (error) {
		console.debug('[useOpenTabNotifier] Notification failed', error);
	}
}
